import type { ErrorEvent, EventHint } from '@sentry/core'
import type { App } from 'vue'
import type { SentryUserSyncResult } from './sentry-user-from-auth'

type SentryVue = typeof import('@sentry/vue')

/** First argument to `Sentry.init` (excludes `undefined`; properties remain optional via `Partial`). */
type SentryVueInitArg = NonNullable<Parameters<SentryVue['init']>[0]>

export type FpSentryBeforeSend = (
  event: ErrorEvent,
  hint: EventHint
) => ErrorEvent | PromiseLike<ErrorEvent | null> | null

/**
 * Same key may fire many times in a row (e.g. repeated connect attempts). Only allow
 * one "send" per key per window; prune stale keys so the Map does not grow forever.
 */
export function createSentryKeyedDedupe(
  dedupeMs: number
): (key: string) => boolean {
  const lastSent = new Map<string, number>()
  return (key: string) => {
    const now = Date.now()
    for (const [k, t] of lastSent) {
      if (now - t > dedupeMs * 4) lastSent.delete(k)
    }
    const prev = lastSent.get(key)
    if (prev !== undefined && now - prev < dedupeMs) return false
    lastSent.set(key, now)
    return true
  }
}

/** Logs exceptions before upload; use as default `beforeSend` for admin/grantee. */
export function fpSentryDefaultBeforeSend(
  event: ErrorEvent,
  hint: EventHint
): ErrorEvent | PromiseLike<ErrorEvent | null> | null {
  if (event.exception) {
    console.error(
      `[Exception handled by Sentry]: (${hint.originalException})`,
      { event, hint }
    )
  }
  return event
}

export function getFpSentryVueInitOptions(
  Sentry: SentryVue,
  input: {
    vueApp: App
    dsn: string | undefined
    environment: string
    tunnel: string | undefined
    beforeSend: FpSentryBeforeSend
  }
): SentryVueInitArg {
  return {
    app: [input.vueApp],
    dsn: input.dsn,
    integrations: [Sentry.browserTracingIntegration()],
    environment: input.environment,
    sampleRate: 1,
    tracesSampleRate: 1.0,
    debug: false,
    tunnel: input.tunnel,
    beforeSend: input.beforeSend,
  }
}

export function registerSentryCspViolationReporter(
  Sentry: SentryVue,
  options: { dsn?: string | null | undefined; dedupeMs?: number }
): void {
  const { dsn, dedupeMs = 15_000 } = options
  if (!dsn) return

  const shouldSendCspViolation = createSentryKeyedDedupe(dedupeMs)
  window.addEventListener('securitypolicyviolation', (e) => {
    const directive = e.effectiveDirective || e.violatedDirective
    const key = `${directive}|${e.blockedURI}`
    if (!shouldSendCspViolation(key)) return

    Sentry.captureMessage('Content Security Policy violation', {
      level: 'warning',
      tags: { csp_violation: 'true' },
      fingerprint: ['csp', directive, e.blockedURI],
      contexts: {
        csp: {
          blockedURI: e.blockedURI,
          columnNumber: e.columnNumber,
          disposition: e.disposition,
          documentURI: e.documentURI,
          effectiveDirective: e.effectiveDirective,
          lineNumber: e.lineNumber,
          originalPolicy: e.originalPolicy,
          referrer: e.referrer,
          sample: e.sample,
          sourceFile: e.sourceFile,
          statusCode: e.statusCode,
          violatedDirective: e.violatedDirective,
        },
      },
    })
  })
}

export function attachSentryVueAppInstrumentation(
  Sentry: SentryVue,
  vueApp: App
): void {
  vueApp.mixin(
    Sentry.createTracingMixins({
      trackComponents: true,
      timeout: 2000,
      hooks: ['activate', 'mount', 'update'],
    })
  )
  Sentry.attachErrorHandler(vueApp, {
    attachProps: true,
    attachErrorHandler: true,
  })
}

/** Apply `getSentryUserSyncResult` output to the Sentry SDK user context. */
export function applySentryUserSyncToSdk(
  Sentry: SentryVue,
  result: SentryUserSyncResult
): void {
  if (result.kind === 'clear') {
    Sentry.setUser(null)
    return
  }
  if (result.kind === 'set') {
    Sentry.setUser(result.user)
  }
}
